// dependencies.
import styled from 'styled-components'
// components.
import { Body } from '../../../Typography'
// utils.
import { theme } from '../../../../styleguide'
// constants.
import { minCellsWidth, COLUMN_LAYOUT_START, COLUMN_LAYOUT_END } from './Item'
import { AUDITORIES_TABLE_HEADER_HEIGHT } from '../../../../constants'
import { useDimensions } from '../../../../js/hooks'

// helpers
const baseUnit = theme.fontSize.x16

// partials.
const Wrapper = styled.li({
  backgroundColor: theme.colors.white,
  borderBottom: `2px solid ${theme.opacity.ash80}`,
  display: 'flex',
  flexDirection: 'row',
  height: AUDITORIES_TABLE_HEADER_HEIGHT,
  maxWidth: '100%',
  position: 'absolute',
  right: 0,
  top: 0,
  width: '100%',
  zIndex: 8,
})

const Cell = styled.div({
  alignItems: 'center',
  backgroundColor: (props) =>
    props.isDynamic ? theme.opacity.success10 : props.isSpecial ? theme.colors.warning : 'none',
  borderRight: (props) =>
    `1px solid ${props.isDynamic ? theme.opacity.black20 : theme.opacity.ash80}`,
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  gap: baseUnit / 2,
  height: '100%',
  justifyContent: 'center',
  padding: 0,
  width: (props) => `${props.width}%`,
  zIndex: 0,
})

const StyledSortLabel = styled(Body)({
  userSelect: 'none',
  textAlign: 'center',
})

const StyledRotateLabel = styled(Body)({
  lineHeight: 0.8,
  minWidth: AUDITORIES_TABLE_HEADER_HEIGHT - 16,
  userSelect: 'none',
  transform: `translate(50%, ${AUDITORIES_TABLE_HEADER_HEIGHT / 2 - 8}px) rotate(-90deg)`,
  transformOrigin: 'center left',
})

// main component.
const AuditReportsDesktopSort = ({ dynamicColumns, style }) => {
  const { width } = useDimensions()

  const cellWidth = 100 / (minCellsWidth + dynamicColumns.length)

  // return content.
  return (
    <Wrapper style={style}>
      {COLUMN_LAYOUT_START.map(({ label, columns, isDynamic }) => {
        return (
          <Cell key={label} width={cellWidth * columns} isDynamic={isDynamic}>
            {isDynamic ? (
              <StyledRotateLabel type="caption">
                <small>{label}</small>
              </StyledRotateLabel>
            ) : (
              <>
                <StyledSortLabel type="caption" color={label === 'icon' ? 'alert' : 'grey'}>
                  {label === 'icon' ? (
                    <svg
                      width={width <= 1320 ? 48 : 72}
                      height={width <= 1320 ? 48 : 72}
                      viewBox="0 0 196 196"
                      fill="currentColor"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M113.603 146.141V147.094C113.603 148.208 113.504 149.198 113.306 150.062C113.119 150.917 112.837 151.641 112.462 152.234C112.098 152.828 111.645 153.276 111.103 153.578C110.561 153.88 109.942 154.031 109.244 154.031C108.515 154.031 107.874 153.88 107.322 153.578C106.78 153.276 106.322 152.828 105.947 152.234C105.582 151.641 105.306 150.917 105.119 150.062C104.942 149.198 104.853 148.208 104.853 147.094V146.141C104.853 145.016 104.942 144.026 105.119 143.172C105.306 142.307 105.582 141.583 105.947 141C106.311 140.417 106.765 139.979 107.306 139.688C107.848 139.385 108.483 139.234 109.212 139.234C109.91 139.234 110.53 139.385 111.072 139.688C111.624 139.979 112.087 140.417 112.462 141C112.837 141.583 113.119 142.307 113.306 143.172C113.504 144.026 113.603 145.016 113.603 146.141Z" />
                      <path d="M130.694 146.047V139.484H134.288C135.058 139.484 135.683 139.651 136.163 139.984C136.642 140.307 136.991 140.734 137.209 141.266C137.439 141.797 137.553 142.375 137.553 143C137.553 143.583 137.439 144.109 137.209 144.578C136.991 145.036 136.642 145.396 136.163 145.656C135.683 145.917 135.058 146.047 134.288 146.047H130.694Z" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M61.5492 10L10 61.5492V134.451L61.5492 186H134.451L186 134.451V61.5492L134.451 10H61.5492ZM136.775 80.7563L137.822 81.6695H137.82C140.453 83.9629 140.742 88.0052 138.463 90.6529L119.75 112.398C118.769 113.537 117.892 114.758 117.081 116.023C114.397 120.212 109.717 123 104.423 123H84.0528C75.7733 123 69 116.185 69 107.855V87.3601C69 87.1859 69.0044 87.0117 69.0104 86.8392C69.0044 86.7354 69 86.6337 69 86.5304V49.2977C69 46.4538 71.3117 44.128 74.1383 44.128H74.5184C77.345 44.128 79.6561 46.4538 79.6561 49.2977V70.5036C79.6561 71.2575 80.265 71.8684 81.0126 71.8684C81.6936 71.8684 82.2452 71.3129 82.2452 70.6283V43.5236C82.2452 40.6796 84.5568 38.3538 87.3835 38.3538H87.7636C90.5902 38.3538 92.9019 40.6796 92.9019 43.5236V70.4108C92.9019 71.2173 93.5517 71.8701 94.3518 71.8701C94.9803 71.8701 95.4909 71.3564 95.4909 70.7239V36.9967C95.4909 34.2483 97.7256 32 100.457 32H101.181C103.912 32 106.147 34.2483 106.147 36.9967V70.502C106.147 71.2563 106.756 71.8684 107.505 71.8684C108.186 71.8684 108.736 71.3129 108.736 70.63V42.4532C108.736 39.6093 111.048 37.2835 113.874 37.2835H114.254C117.081 37.2835 119.393 39.6093 119.393 42.4532V85.7453C119.45 86.276 119.479 86.8156 119.479 87.3601C119.479 88.6601 121.082 89.2617 121.928 88.2793L127.846 81.4025C130.126 78.7547 134.144 78.4629 136.775 80.7563ZM65.4531 151.953C65.4531 151.599 65.401 151.281 65.2969 151C65.1927 150.708 65 150.438 64.7188 150.188C64.4375 149.938 64.0312 149.688 63.5 149.438C62.9792 149.177 62.2917 148.906 61.4375 148.625C60.4375 148.292 59.474 147.917 58.5469 147.5C57.6198 147.073 56.7917 146.578 56.0625 146.016C55.3333 145.443 54.7552 144.776 54.3281 144.016C53.901 143.245 53.6875 142.349 53.6875 141.328C53.6875 140.349 53.9062 139.469 54.3438 138.688C54.7812 137.896 55.3906 137.224 56.1719 136.672C56.9635 136.109 57.8906 135.682 58.9531 135.391C60.0156 135.089 61.1771 134.938 62.4375 134.938C64.1042 134.938 65.5729 135.229 66.8438 135.812C68.125 136.385 69.125 137.203 69.8438 138.266C70.5729 139.318 70.9375 140.562 70.9375 142H65.4844C65.4844 141.427 65.3646 140.922 65.125 140.484C64.8958 140.047 64.5469 139.703 64.0781 139.453C63.6094 139.203 63.0208 139.078 62.3125 139.078C61.625 139.078 61.0469 139.182 60.5781 139.391C60.1094 139.599 59.7552 139.88 59.5156 140.234C59.276 140.578 59.1562 140.958 59.1562 141.375C59.1562 141.719 59.25 142.031 59.4375 142.312C59.6354 142.583 59.9115 142.839 60.2656 143.078C60.6302 143.318 61.0677 143.547 61.5781 143.766C62.099 143.984 62.6823 144.198 63.3281 144.406C64.5365 144.792 65.6094 145.224 66.5469 145.703C67.4948 146.172 68.2917 146.708 68.9375 147.312C69.5938 147.906 70.0885 148.583 70.4219 149.344C70.7656 150.104 70.9375 150.964 70.9375 151.922C70.9375 152.943 70.7396 153.849 70.3438 154.641C69.9479 155.432 69.3802 156.104 68.6406 156.656C67.901 157.198 67.0156 157.609 65.9844 157.891C64.9531 158.172 63.8021 158.312 62.5312 158.312C61.3646 158.312 60.2135 158.167 59.0781 157.875C57.9531 157.573 56.9323 157.115 56.0156 156.5C55.099 155.875 54.3646 155.078 53.8125 154.109C53.2708 153.13 53 151.969 53 150.625H58.5C58.5 151.292 58.5885 151.854 58.7656 152.312C58.9427 152.771 59.2031 153.141 59.5469 153.422C59.8906 153.693 60.3125 153.891 60.8125 154.016C61.3125 154.13 61.8854 154.188 62.5312 154.188C63.2292 154.188 63.7917 154.089 64.2188 153.891C64.6458 153.682 64.9583 153.411 65.1562 153.078C65.3542 152.734 65.4531 152.359 65.4531 151.953ZM119.166 147.094V146.172C119.166 144.432 118.921 142.87 118.431 141.484C117.942 140.099 117.254 138.922 116.369 137.953C115.483 136.974 114.431 136.229 113.212 135.719C112.004 135.198 110.671 134.938 109.212 134.938C107.744 134.938 106.4 135.198 105.181 135.719C103.973 136.229 102.926 136.974 102.041 137.953C101.166 138.922 100.483 140.099 99.9937 141.484C99.5146 142.87 99.275 144.432 99.275 146.172V147.094C99.275 148.833 99.5146 150.396 99.9937 151.781C100.483 153.156 101.171 154.333 102.056 155.312C102.952 156.281 104.004 157.026 105.212 157.547C106.431 158.057 107.775 158.312 109.244 158.312C110.702 158.312 112.035 158.057 113.244 157.547C114.452 157.026 115.499 156.281 116.384 155.312C117.27 154.333 117.952 153.156 118.431 151.781C118.921 150.396 119.166 148.833 119.166 147.094ZM130.694 150.281H134.288C136.11 150.281 137.678 149.979 138.991 149.375C140.314 148.76 141.329 147.906 142.038 146.812C142.746 145.719 143.1 144.438 143.1 142.969C143.1 141.479 142.746 140.156 142.038 139C141.329 137.833 140.314 136.917 138.991 136.25C137.678 135.583 136.11 135.25 134.288 135.25H125.209V158H130.694V150.281ZM87.9812 139.484V158H82.4969V139.484H75.8094V135.25H94.825V139.484H87.9812Z"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M59.4781 0C58.1521 0 56.8803 0.526794 55.9426 1.46446L1.46448 55.9426C0.526794 56.8803 0 58.1521 0 59.4781V136.522C0 137.848 0.526794 139.12 1.46448 140.057L55.9426 194.536C56.8803 195.473 58.1521 196 59.4781 196H136.522C137.848 196 139.12 195.473 140.057 194.536L194.536 140.057C195.473 139.12 196 137.848 196 136.522V59.4781C196 58.1521 195.473 56.8803 194.536 55.9426L140.057 1.46446C139.12 0.526779 137.848 0 136.522 0H59.4781ZM58.0137 6.46446C58.9514 5.52678 60.2231 5 61.5492 5H134.451C135.777 5 137.049 5.52678 137.986 6.46446L189.536 58.0137C190.473 58.9514 191 60.2231 191 61.5492V134.451C191 135.777 190.473 137.049 189.536 137.986L137.986 189.536C137.049 190.473 135.777 191 134.451 191H61.5492C60.2231 191 58.9514 190.473 58.0137 189.536L6.46448 137.986C5.52679 137.049 5 135.777 5 134.451V61.5492C5 60.2231 5.52679 58.9514 6.46448 58.0137L58.0137 6.46446Z"
                      />
                    </svg>
                  ) : (
                    <small>{label}</small>
                  )}
                </StyledSortLabel>
              </>
            )}
          </Cell>
        )
      })}
      {dynamicColumns.map(({ label }) => {
        return (
          <Cell key={label} width={cellWidth} isDynamic>
            <StyledRotateLabel type="caption">
              <small>{label}</small>
            </StyledRotateLabel>
          </Cell>
        )
      })}
      {COLUMN_LAYOUT_END.map(({ label, columns, isDynamic, isSpecial }) => {
        return (
          <Cell key={label} width={cellWidth * columns} isSpecial={isSpecial}>
            {isDynamic ? (
              <StyledRotateLabel type="caption">
                <small dangerouslySetInnerHTML={{ __html: label }} />
              </StyledRotateLabel>
            ) : (
              <StyledSortLabel type="caption">
                <small dangerouslySetInnerHTML={{ __html: label }} />
              </StyledSortLabel>
            )}
          </Cell>
        )
      })}
    </Wrapper>
  )
}

export default AuditReportsDesktopSort
